import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Snackbar,
  Alert,
  Drawer,
  TableSortLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  MenuItem,
  TextField,
  Card,
  CardContent,
  Skeleton,
  Chip,
} from '@mui/material';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import Sidebar from '../components/Sidebar';
import { fetchBankAccounts } from '../api/services';

// Shared look for the Finance pages (design tokens + small UI helpers)
const fin = {
  primary: '#009db9',
  primaryDark: '#007a8a',
  primarySoft: '#e6f6f9',
  text: '#1f2933',
  muted: '#6b7280',
  border: '#e5e7eb',
  surface: '#f7f9fb',
  success: '#2e7d32',
  successSoft: '#e8f5e9',
  danger: '#d32f2f',
  dangerSoft: '#ffebee',
  warning: '#b26a00',
  warningSoft: '#fff4e5',
  radius: '16px',
  shadow: '0 4px 20px rgba(16, 24, 40, 0.06)',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: fin.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: fin.primary,
    },
    '&.Mui-disabled': { backgroundColor: '#f3f5f7' },
  },
  '& label.Mui-focused': { color: fin.primary },
};

const headCellSx = {
  fontWeight: 700,
  color: fin.primaryDark,
  backgroundColor: fin.primarySoft,
  fontSize: '0.8rem',
  py: 1.5,
  borderBottom: `1px solid ${fin.border}`,
};

const bodyCellSx = {
  color: fin.text,
  py: 1.75,
  fontSize: '0.875rem',
  borderBottom: `1px solid ${fin.border}`,
};

const mobileCardSx = {
  border: `1px solid ${fin.border}`,
  borderRadius: '12px',
  p: 2,
  backgroundColor: '#fff',
};

const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0][0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toUpperCase();
};

const FinancePageHeader = ({ title }) => (
  <Box sx={{ mb: { xs: 3, md: 4 } }}>
    <Typography
      variant="h4"
      sx={{
        fontWeight: 'bold',
        color: fin.text,
        letterSpacing: 0.5,
        fontSize: { xs: '1.5rem', sm: '1.85rem', md: '2.125rem' },
        position: 'relative',
        '&::after': {
          content: '""',
          display: 'block',
          backgroundColor: fin.primary,
          height: '4px',
          width: { xs: '80px', sm: '100px' },
          position: 'absolute',
          left: 0,
          bottom: '-8px',
        },
      }}
    >
      {title}
    </Typography>
  </Box>
);

const FinanceMenuButton = ({ onClick }) => (
  <Box
    sx={{
      display: { xs: 'block', md: 'none' },
      position: 'sticky',
      top: { xs: '64px', sm: '80px' },
      zIndex: 1100,
      mb: 2,
    }}
  >
    <Tooltip title="Menu" arrow>
      <IconButton
        onClick={onClick}
        sx={{
          background: 'linear-gradient(135deg, #009db9 0%, #007a8a 100%)',
          color: '#fff',
          borderRadius: '12px',
          p: 1.2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          '&:hover': {
            background: 'linear-gradient(135deg, #007a8a 0%, #005b66 100%)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
          },
          transition: 'all 0.3s ease',
        }}
        aria-label="toggle sidebar"
      >
        <MenuIcon sx={{ fontSize: '1.6rem' }} />
      </IconButton>
    </Tooltip>
  </Box>
);

const SectionCard = ({ title, action, children }) => (
  <Card
    sx={{
      mb: { xs: 2.5, md: 4 },
      borderRadius: fin.radius,
      border: `1px solid ${fin.border}`,
      backgroundColor: '#fff',
      boxShadow: fin.shadow,
      overflow: 'visible',
    }}
  >
    <CardContent
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        '&:last-child': { pb: { xs: 2, sm: 3, md: 4 } },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: fin.text,
            pl: 1.5,
            borderLeft: `4px solid ${fin.primary}`,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

const StatGrid = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: { xs: 1.5, md: 2 },
      mb: { xs: 2.5, md: 4 },
    }}
  >
    {children}
  </Box>
);

const statTones = {
  primary: { bg: fin.primarySoft, color: fin.primary },
  success: { bg: fin.successSoft, color: fin.success },
  warning: { bg: fin.warningSoft, color: fin.warning },
  danger: { bg: fin.dangerSoft, color: fin.danger },
};

const StatCard = ({ label, value, hint, icon: Icon, tone = 'primary' }) => {
  const t = statTones[tone] || statTones.primary;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 2,
        minWidth: 0,
        borderRadius: '14px',
        border: `1px solid ${fin.border}`,
        backgroundColor: '#fff',
        boxShadow: fin.shadow,
      }}
    >
      {Icon && (
        <Box
          sx={{
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: '10px',
            backgroundColor: t.bg,
            color: t.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon fontSize="small" />
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: fin.muted }} noWrap>
          {label}
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            color: fin.text,
            fontSize: { xs: '1.05rem', md: '1.2rem' },
            lineHeight: 1.3,
            wordBreak: 'break-word',
          }}
        >
          {value}
        </Typography>
        {hint && (
          <Typography variant="caption" sx={{ color: fin.muted }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const chipTones = {
  success: { bg: fin.successSoft, color: fin.success },
  danger: { bg: fin.dangerSoft, color: fin.danger },
  warning: { bg: fin.warningSoft, color: fin.warning },
  info: { bg: fin.primarySoft, color: fin.primaryDark },
  neutral: { bg: '#eef1f4', color: '#4b5563' },
};

const PillChip = ({ label, tone = 'neutral' }) => {
  const t = chipTones[tone] || chipTones.neutral;
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        fontWeight: 700,
        borderRadius: '8px',
        backgroundColor: t.bg,
        color: t.color,
      }}
    />
  );
};

const EmptyState = ({ icon: Icon = InboxOutlinedIcon, title, message, action }) => (
  <Box sx={{ textAlign: 'center', py: { xs: 5, md: 7 }, px: 2 }}>
    <Box
      sx={{
        width: 64,
        height: 64,
        mx: 'auto',
        mb: 2,
        borderRadius: '50%',
        backgroundColor: fin.primarySoft,
        color: fin.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon sx={{ fontSize: 32 }} />
    </Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: fin.text }}>
      {title}
    </Typography>
    {message && (
      <Typography
        variant="body2"
        sx={{ color: fin.muted, mt: 0.5, maxWidth: 360, mx: 'auto' }}
      >
        {message}
      </Typography>
    )}
    {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
  </Box>
);

const ListSkeleton = ({ rows = 5 }) => (
  <Box
    role="status"
    aria-label="Loading"
    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
  >
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} variant="rounded" height={56} animation="wave" />
    ))}
  </Box>
);


// Small label/value pair used in the details dialog
const DetailItem = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="body2" sx={{ color: fin.muted, mb: 0.25 }}>
      {label}
    </Typography>
    <Typography sx={{ color: fin.text, fontWeight: 600, wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

const detailGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
  gap: 2.5,
};

const BankManagement = () => {
  const [bankDetails, setBankDetails] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('fullName');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  useEffect(() => {
    const fetchBankAccount = async () => {
      try {
        const response = await fetchBankAccounts();
        console.log('Full bank accounts response:', response);
        
        // Try multiple possible data structures
        let accountsData = [];
        
        // Check if response has data.content structure (most common)
        if (response?.data?.content && Array.isArray(response.data.content)) {
          accountsData = response.data.content;
        } 
        // Check if response is directly an array
        else if (Array.isArray(response)) {
          accountsData = response;
        }
        // Check if response has a data property that's an array
        else if (response?.data && Array.isArray(response.data)) {
          accountsData = response.data;
        }
        // Check if response has a content property that's an array
        else if (response?.content && Array.isArray(response.content)) {
          accountsData = response.content;
        }
        // If it's a single account object
        else if (response && typeof response === 'object' && response.id) {
          accountsData = [response];
        }
        
        console.log('Extracted accountsData:', accountsData);
        
        if (!Array.isArray(accountsData) || accountsData.length === 0) {
          console.log('No bank accounts found in response');
          setBankDetails([]);
          setLoading(false);
          return;
        }
        
        const mappedAccounts = accountsData.map((account, index) => ({
          id: account.id || `acc-${Date.now()}-${index}`,
          fullName: account.fullName || account.name || 'N/A',
          bankName: account.bankName || 'N/A',
          accountBranch: account.accountBranch || 'N/A',
          accountNumber: account.accountNumber || 'N/A',
          accountType: account.accountType || 'Mobile Money',
          accountCurrency: account.accountCurrency || 'KES',
          mobileNumber: account.mobileNumber || 'N/A',
          createdAt: account.createdAt || new Date().toISOString(),
          userId: account.userId || 'N/A',
        }));
        
        console.log('Mapped accounts:', mappedAccounts);
        setBankDetails(mappedAccounts);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching bank accounts:', error);
        setSnackbarMessage(error.message || 'Error fetching bank accounts');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
        setBankDetails([]);
      }
    };

    fetchBankAccount();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const sortedBankDetails = [...bankDetails].sort((a, b) => {
    if (orderBy === 'createdAt') {
      return order === 'asc'
        ? new Date(a[orderBy]) - new Date(b[orderBy])
        : new Date(b[orderBy]) - new Date(a[orderBy]);
    }
    return order === 'asc'
      ? a[orderBy].localeCompare(b[orderBy])
      : b[orderBy].localeCompare(a[orderBy]);
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenDetails = (account) => {
    setSelectedAccount(account);
    setDetailsModalOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsModalOpen(false);
    setSelectedAccount(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Format bank account display
  const formatBankAccountDisplay = (account) => {
    return `${account.bankName} - ${account.accountNumber}`;
  };

  // Get display account type - show "Mobile Money" if empty
  const getDisplayAccountType = (accountType) => {
    return accountType || 'Mobile Money';
  };

  // Updated table headers for the requested columns
  const tableHeaders = [
    { label: 'Full name', property: 'fullName' },
    { label: 'Bank account', property: 'bankName' },
    { label: 'Account type', property: 'accountType' },
    { label: 'Action', property: 'action' },
  ];

  // Sort options for the mobile / tablet card view (reuses handleRequestSort)
  const sortOptions = tableHeaders.filter((h) => h.property !== 'action');

  // Summary figures (presentation only)
  const mobileMoneyCount = bankDetails.filter(
    (a) => getDisplayAccountType(a.accountType) === 'Mobile Money'
  ).length;
  const otherAccountsCount = bankDetails.length - mobileMoneyCount;

  const pagedBankDetails = sortedBankDetails.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const accountTypeChip = (accountType) => {
    const label = getDisplayAccountType(accountType);
    return <PillChip label={label} tone={label === 'Mobile Money' ? 'success' : 'info'} />;
  };

  const detailsButton = (detail, fullWidth) => (
    <Button
      onClick={() => handleOpenDetails(detail)}
      startIcon={<VisibilityOutlinedIcon fontSize="small" />}
      variant="outlined"
      size="small"
      fullWidth={fullWidth}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: '8px',
        color: fin.primary,
        borderColor: fin.primary,
        '&:hover': {
          borderColor: fin.primaryDark,
          backgroundColor: fin.primarySoft,
        },
      }}
    >
      Details
    </Button>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', overflowX: 'hidden' }}>
      <Box
        sx={{
          maxWidth: { xs: '100%', sm: '100%', md: '1200px' },
          mx: 'auto',
          width: '100%',
          flexGrow: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: { xs: 'none', md: 'block' }, width: { md: 250 } }}>
          <Sidebar />
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            p: { xs: 2, md: 4 },
            pt: { xs: '50px', sm: '60px', md: '120px' },
            backgroundColor: '#fff',
            minHeight: '100vh',
            ml: { md: '20px' },
            pr: { xs: 1, sm: 2, md: 3 },
            position: 'relative',
            boxSizing: 'border-box',
          }}
        >
          <FinanceMenuButton onClick={handleDrawerToggle} />

          <FinancePageHeader
            title="Bank Management"
          />

          <StatGrid>
            <StatCard
              label="Total accounts"
              value={bankDetails.length.toLocaleString()}
              icon={AccountBalanceOutlinedIcon}
            />
            <StatCard
              label="Mobile money"
              value={mobileMoneyCount.toLocaleString()}
              icon={PhoneAndroidOutlinedIcon}
              tone="success"
            />
            <StatCard
              label="Bank & other"
              value={otherAccountsCount.toLocaleString()}
              icon={CreditCardOutlinedIcon}
              tone="warning"
            />
          </StatGrid>

          <SectionCard
            title="Bank details"
            action={
              // Sort control for the card view (the table sorts from its headers)
              <Box
                sx={{
                  display: { xs: 'flex', lg: 'none' },
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <TextField
                  select
                  size="small"
                  label="Sort by"
                  value={orderBy}
                  onChange={(e) => {
                    if (e.target.value !== orderBy) handleRequestSort(e.target.value);
                  }}
                  sx={{ ...fieldSx, flexGrow: 1, minWidth: 150 }}
                >
                  {sortOptions.map((o) => (
                    <MenuItem key={o.property} value={o.property}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Tooltip title={order === 'asc' ? 'Ascending' : 'Descending'}>
                  <IconButton
                    onClick={() => handleRequestSort(orderBy)}
                    aria-label="Toggle sort direction"
                    sx={{
                      border: `1px solid ${fin.border}`,
                      borderRadius: '10px',
                      color: fin.primary,
                    }}
                  >
                    {order === 'asc' ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            }
          >
            {loading ? (
              <ListSkeleton rows={rowsPerPage} />
            ) : sortedBankDetails.length === 0 ? (
              <EmptyState
                icon={AccountBalanceOutlinedIcon}
                title="No bank accounts found"
                message="Accounts added for users will show up here."
              />
            ) : (
              <>
                {/* Desktop table */}
                <Box
                  sx={{
                    display: { xs: 'none', lg: 'block' },
                    overflowX: 'auto',
                    border: `1px solid ${fin.border}`,
                    borderRadius: '12px',
                  }}
                >
                  <Table sx={{ minWidth: 560 }}>
                    <TableHead>
                      <TableRow>
                        {tableHeaders.map((header) => (
                          <TableCell
                            key={header.label}
                            sx={headCellSx}
                            sortDirection={orderBy === header.property ? order : false}
                          >
                            {header.property !== 'action' ? (
                              <TableSortLabel
                                active={orderBy === header.property}
                                direction={orderBy === header.property ? order : 'asc'}
                                onClick={() => handleRequestSort(header.property)}
                                sx={{
                                  '&.Mui-active': { color: fin.text },
                                  '& .MuiTableSortLabel-icon': {
                                    color: `${fin.primary} !important`,
                                  },
                                }}
                              >
                                {header.label}
                              </TableSortLabel>
                            ) : (
                              header.label
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedBankDetails.map((detail) => (
                        <TableRow
                          key={detail.id}
                          hover
                          sx={{ '&:last-child td': { borderBottom: 0 } }}
                        >
                          <TableCell sx={bodyCellSx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar
                                sx={{
                                  width: 34,
                                  height: 34,
                                  fontSize: '0.8rem',
                                  fontWeight: 700,
                                  backgroundColor: fin.primarySoft,
                                  color: fin.primaryDark,
                                }}
                              >
                                {getInitials(detail.fullName)}
                              </Avatar>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.875rem',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {detail.fullName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>
                            {formatBankAccountDisplay(detail)}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            {accountTypeChip(detail.accountType)}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>{detailsButton(detail, false)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>

                {/* Mobile / tablet cards */}
                <Box
                  sx={{
                    display: { xs: 'flex', lg: 'none' },
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  {pagedBankDetails.map((detail) => (
                    <Box key={detail.id} sx={mobileCardSx}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              fontSize: '0.9rem',
                              fontWeight: 700,
                              backgroundColor: fin.primarySoft,
                              color: fin.primaryDark,
                            }}
                          >
                            {getInitials(detail.fullName)}
                          </Avatar>
                          <Typography sx={{ fontWeight: 700, color: fin.text }} noWrap>
                            {detail.fullName}
                          </Typography>
                        </Box>
                        {accountTypeChip(detail.accountType)}
                      </Box>
                      <Box
                        sx={{
                          mt: 1.5,
                          mb: 1.5,
                          p: 1.5,
                          borderRadius: '10px',
                          backgroundColor: fin.surface,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: fin.muted }}>
                          Bank account
                        </Typography>
                        <Typography sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                          {formatBankAccountDisplay(detail)}
                        </Typography>
                      </Box>
                      {detailsButton(detail, true)}
                    </Box>
                  ))}
                </Box>
              </>
            )}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={sortedBankDetails.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                mt: 2,
                color: fin.text,
                '& .MuiTablePagination-toolbar': {
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'center', sm: 'flex-end' },
                  px: 0,
                },
                '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } },
              }}
            />
          </SectionCard>
        </Box>
      </Box>

      {/* Details Modal */}
      <Dialog 
        open={detailsModalOpen} 
        onClose={handleCloseDetails} 
        maxWidth="sm" 
        fullWidth 
        PaperProps={{ sx: { borderRadius: '16px', m: { xs: 2, sm: 4 } } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            fontWeight: 700,
            color: fin.text,
            pb: 1.5,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: fin.primarySoft,
              color: fin.primary,
            }}
          >
            <AccountBalanceOutlinedIcon fontSize="small" />
          </Box>
          Bank account details
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          {selectedAccount && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    fontWeight: 700,
                    backgroundColor: fin.primarySoft,
                    color: fin.primaryDark,
                  }}
                >
                  {getInitials(selectedAccount.fullName)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, color: fin.text, wordBreak: 'break-word' }}>
                    {selectedAccount.fullName}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>{accountTypeChip(selectedAccount.accountType)}</Box>
                </Box>
              </Box>

              <Divider />

              <Box sx={detailGridSx}>
                <DetailItem label="Bank name" value={selectedAccount.bankName} />
                <DetailItem label="Account number" value={selectedAccount.accountNumber} />
                <DetailItem label="Account branch" value={selectedAccount.accountBranch} />
                <DetailItem
                  label="Account currency"
                  value={selectedAccount.accountCurrency}
                />
                <DetailItem label="Mobile number" value={selectedAccount.mobileNumber} />
                <DetailItem
                  label="Account type"
                  value={getDisplayAccountType(selectedAccount.accountType)}
                />
              </Box>

              <Divider />

              <Box sx={detailGridSx}>
                <DetailItem label="User ID" value={selectedAccount.userId} />
                <DetailItem
                  label="Created date"
                  value={formatDate(selectedAccount.createdAt)}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseDetails}
            sx={{ color: fin.muted, textTransform: 'none', fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          zIndex: 1100,
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 250,
            top: { xs: '64px', sm: '80px' },
            height: 'calc(100% - 64px)',
            borderRight: 'none',
          },
        }}
      >
        <Sidebar onItemClick={handleDrawerToggle} />
      </Drawer>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: '100%',
            maxWidth: { xs: '90vw', sm: '500px' },
            borderRadius: '10px',
            boxSizing: 'border-box',
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BankManagement;